// ======================================
// Admin Analytics Module
// ======================================

import { apiRequest } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAnalyticsAdmin() {
  const content = `
      <div class="admin-page-header">
        <div>
          <h2 class="admin-page-title" style="margin:0;">Analytics & Reports</h2>
          <p class="admin-page-subtitle">Track website performance, conversions, and lead sources</p>
        </div>
        <select id="analytics-period" class="form-input" style="max-width:180px;">
          <option value="7">Last 7 days</option>
          <option value="30" selected>Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:var(--space-4);margin-bottom:var(--space-8);">
        <div class="admin-card stat-card" style="text-align:center;padding:var(--space-6);">
          <p style="font-size:13px;color:var(--color-text-muted);margin:0;">Page Views</p>
          <p id="stat-pageviews" style="font-size:28px;font-weight:700;color:var(--color-primary);margin:var(--space-2) 0 0;">—</p>
        </div>
        <div class="admin-card stat-card" style="text-align:center;padding:var(--space-6);">
          <p style="font-size:13px;color:var(--color-text-muted);margin:0;">Unique Visitors</p>
          <p id="stat-visitors" style="font-size:28px;font-weight:700;color:var(--color-accent);margin:var(--space-2) 0 0;">—</p>
        </div>
        <div class="admin-card stat-card" style="text-align:center;padding:var(--space-6);">
          <p style="font-size:13px;color:var(--color-text-muted);margin:0;">CTA Clicks</p>
          <p id="stat-clicks" style="font-size:28px;font-weight:700;color:var(--color-success);margin:var(--space-2) 0 0;">—</p>
        </div>
        <div class="admin-card stat-card" style="text-align:center;padding:var(--space-6);">
          <p style="font-size:13px;color:var(--color-text-muted);margin:0;">Conversion Rate</p>
          <p id="stat-conversion" style="font-size:28px;font-weight:700;color:var(--color-warning);margin:var(--space-2) 0 0;">—</p>
        </div>
      </div>

      <!-- Two Column Layout -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-6);">
        <!-- Top Pages -->
        <div class="admin-card" style="padding:var(--space-6);">
          <h3 style="margin:0 0 var(--space-4);font-size:16px;">Top Pages</h3>
          <div id="top-pages" style="display:flex;flex-direction:column;gap:var(--space-3);">
            <p style="color:var(--color-text-muted);font-size:13px;">Loading...</p>
          </div>
        </div>

        <!-- Lead Sources -->
        <div class="admin-card" style="padding:var(--space-6);">
          <h3 style="margin:0 0 var(--space-4);font-size:16px;">Lead Sources</h3>
          <div id="lead-sources" style="display:flex;flex-direction:column;gap:var(--space-3);">
            <p style="color:var(--color-text-muted);font-size:13px;">Loading...</p>
          </div>
        </div>
      </div>

      <!-- Recent Events -->
      <div class="admin-card" style="margin-top:var(--space-6);padding:var(--space-6);">
        <h3 style="margin:0 0 var(--space-4);font-size:16px;">Recent Events</h3>
        <div id="recent-events" style="max-height:300px;overflow-y:auto;">
          <p style="color:var(--color-text-muted);font-size:13px;">Loading...</p>
        </div>
      </div>
    `;

  return renderAdminLayout('analytics', 'Analytics & Reports', content);
}

export function initAnalyticsAdmin() {
  initAdminLayout();

  async function loadAnalytics() {
    const periodSelect = document.getElementById('analytics-period');
    const period = periodSelect ? periodSelect.value : '30';

    try {
      const data = await apiRequest(`/analytics?period=${period}`);
      const stats = data.data || data || {};

      const setStat = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.textContent = value;
      };

      setStat('stat-pageviews', (stats.summary?.total_events || stats.page_views || 0).toLocaleString());
      setStat('stat-visitors', (stats.summary?.unique_pages || stats.unique_visitors || 0).toLocaleString());
      setStat('stat-clicks', (stats.cta_clicks || 0).toLocaleString());

      const views = stats.summary?.total_events || stats.page_views || 0;
      const clicks = stats.cta_clicks || 0;
      const convRate = views > 0 ? ((clicks / views) * 100).toFixed(1) : '0.0';
      setStat('stat-conversion', convRate + '%');

      // Top pages
      const topPages = stats.top_pages || [];
      const tpContainer = document.getElementById('top-pages');
      if (tpContainer) {
        if (topPages.length) {
          const maxViews = Math.max(...topPages.map(p => p.views));
          tpContainer.innerHTML = topPages.map(p => `
            <div style="display:flex;align-items:center;gap:var(--space-3);">
              <div style="flex:1;min-width:0;">
                <p style="margin:0;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.page || p.page_url || '/'}</p>
                <div style="height:4px;background:var(--color-border);border-radius:2px;margin-top:4px;">
                  <div style="height:100%;width:${(p.views / maxViews * 100)}%;background:var(--color-primary);border-radius:2px;"></div>
                </div>
              </div>
              <span style="font-size:13px;font-weight:600;min-width:40px;text-align:right;">${p.views}</span>
            </div>
          `).join('');
        } else {
          tpContainer.innerHTML = '<p style="color:var(--color-text-muted);font-size:13px;">No data yet</p>';
        }
      }

      // Lead sources
      const sources = stats.lead_sources || [];
      const lsContainer = document.getElementById('lead-sources');
      if (lsContainer) {
        if (sources.length) {
          const colors = ['#1B5E20', '#C8A94E', '#2196F3', '#FF5722', '#9C27B0', '#00BCD4'];
          const total = sources.reduce((sum, s) => sum + parseInt(s.count), 0);
          lsContainer.innerHTML = sources.map((s, i) => `
            <div style="display:flex;align-items:center;gap:var(--space-3);">
              <div style="width:12px;height:12px;border-radius:50%;background:${colors[i % colors.length]};flex-shrink:0;"></div>
              <span style="flex:1;font-size:13px;text-transform:capitalize;">${(s.source || 'unknown').replace(/_/g, ' ')}</span>
              <span style="font-size:13px;font-weight:600;">${s.count}</span>
              <span style="font-size:12px;color:var(--color-text-muted);">${total ? ((s.count / total) * 100).toFixed(0) : 0}%</span>
            </div>
          `).join('');
        } else {
          lsContainer.innerHTML = '<p style="color:var(--color-text-muted);font-size:13px;">No data yet</p>';
        }
      }

      // Recent events
      const events = stats.recent_events || [];
      const container = document.getElementById('recent-events');
      if (container) {
        if (events.length) {
          container.innerHTML = `<table class="admin-table" style="font-size:13px;">
            <thead><tr><th>Type</th><th>Page</th><th>Time</th></tr></thead>
            <tbody>${events.map(e => `
              <tr>
                <td><span class="badge badge-info">${e.event_type}</span></td>
                <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${e.page_url || '—'}</td>
                <td>${new Date(e.created_at).toLocaleString()}</td>
              </tr>
            `).join('')}</tbody>
          </table>`;
        } else {
          container.innerHTML = '<p style="color:var(--color-text-muted);font-size:13px;">No events recorded yet</p>';
        }
      }
    } catch (e) {
      console.error('Failed to load analytics:', e);
      const setStat = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.textContent = value;
      };
      setStat('stat-pageviews', '—');
      setStat('stat-visitors', '—');
      setStat('stat-clicks', '—');
      setStat('stat-conversion', '—');
    }
  }

  document.getElementById('analytics-period')?.addEventListener('change', loadAnalytics);
  loadAnalytics();
}
